import {FC, useEffect, useId, useRef, useState} from 'react'
import {KTIcon} from './KTIcon'

/*
 * Escape closes the topmost dialog of the panel, the way its own close button does.
 *
 * Most panel dialogs are drawn in place (`.modal.show.d-block`) rather than through
 * react-bootstrap, so the keyboard could not close them. This one listener sits on window,
 * after every other listener: whatever uses Escape first (an open select menu, the date
 * picker's calendar, a help tooltip, a dialog with its own Escape handling) marks the key
 * handled and the dialog stays. react-bootstrap's Modal is portalled to <body> and closes
 * itself through onHide.
 *
 * A dialog the user has typed in or picked a value in asks before it closes, so a stray
 * Escape never throws away a half-filled form.
 */

const OPEN_DIALOG = '.modal.show'
/** Marks the control Escape presses when the dialog's close button is not in its header. */
const EXPLICIT_CLOSE = '[data-dialog-close]'
/** Looked for in the header only, so a row's × in the body is never pressed. */
const HEADER_CLOSE = '.btn-close, [data-bs-dismiss], .ki-cross, .la-times, [aria-label^="بستن"]'
/** Looked for anywhere; Alert's cancel button sits on the alert that is the whole dialog. */
const DIALOG_CLOSE =
  '[data-bs-dismiss="modal"], .modal-content > .alert > [data-bs-dismiss="alert"]'
const CLICKABLE = 'button, a, [role="button"], .btn'
const REACT_SELECT_OPTION = '[id^="react-select-"][id*="-option-"]'
const SELECT_PICK_KEYS = ['Enter', 'Tab', ' ']

const editedDialogs = new WeakSet<Element>()
/** Escapes react-select swallowed although its menu was closed; they still close the dialog. */
const escapesFromClosedSelect = new WeakSet<Event>()

const isElement = (target: EventTarget | null): target is HTMLElement =>
  target instanceof HTMLElement

export const topmostDialog = (): HTMLElement | undefined => {
  const open = Array.from(document.querySelectorAll<HTMLElement>(OPEN_DIALOG)).filter(
    (dialog) => window.getComputedStyle(dialog).display !== 'none'
  )
  return open[open.length - 1]
}

/** react-bootstrap portals its Modal to <body> and closes it, with whatever it holds, itself. */
const isInReactBootstrapModal = (dialog: HTMLElement) =>
  Array.from(document.body.children).some(
    (child) => child.classList.contains('modal') && child.contains(dialog)
  )

const firstControl = (dialog: HTMLElement, scopes: Element[], selector: string) => {
  for (const scope of scopes) {
    for (const match of Array.from(scope.querySelectorAll<HTMLElement>(selector))) {
      const control = match.closest<HTMLElement>(CLICKABLE) ?? match
      if (control.closest('.modal') === dialog) return control
    }
  }
  return undefined
}

export const findDialogCloseControl = (dialog: HTMLElement): HTMLElement | undefined =>
  firstControl(dialog, [dialog], EXPLICIT_CLOSE) ??
  firstControl(dialog, Array.from(dialog.querySelectorAll('.modal-header')), HEADER_CLOSE) ??
  firstControl(dialog, [dialog], DIALOG_CLOSE)

const isUnavailable = (control: HTMLElement) =>
  control.matches(':disabled, .disabled, [aria-disabled="true"]')

/** Records a value the user chose in a field that raises no input event (e.g. the date picker). */
export const noteDialogEdited = (element: Element | null | undefined) => {
  const dialog = element?.closest('.modal')
  if (dialog) editedDialogs.add(dialog)
}

const onFieldEdited = (event: Event) => {
  const field = event.target
  if (!isElement(field)) return
  // A search box or a select's search text filters; it changes nothing yet.
  if (field.matches('input[type="search"], [role="combobox"]')) return
  if (field.matches('input, select, textarea') || field.isContentEditable) noteDialogEdited(field)
}

const onSelectOptionClicked = (event: MouseEvent) => {
  // react-select portals its menu to <body>, outside the dialog that opened it.
  if (isElement(event.target) && event.target.closest(REACT_SELECT_OPTION)) {
    noteDialogEdited(topmostDialog())
  }
}

const onKeyDownCapture = (event: KeyboardEvent) => {
  const target = event.target
  if (!isElement(target) || !target.matches('[role="combobox"]')) return
  const expanded = target.getAttribute('aria-expanded') === 'true'
  if (expanded && SELECT_PICK_KEYS.includes(event.key)) noteDialogEdited(target)
  if (!expanded && event.key === 'Escape') escapesFromClosedSelect.add(event)
}

const UnsavedChangesDialog: FC<{onKeepEditing: () => void; onDiscard: () => void}> = ({
  onKeepEditing,
  onDiscard,
}) => {
  const titleId = useId()
  const descriptionId = useId()
  const keepEditingButton = useRef<HTMLButtonElement>(null)
  const discardButton = useRef<HTMLButtonElement>(null)
  const keepEditingRef = useRef(onKeepEditing)
  keepEditingRef.current = onKeepEditing

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    keepEditingButton.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        keepEditingRef.current()
        return
      }
      if (event.key !== 'Tab') return
      event.preventDefault()
      const next =
        document.activeElement === keepEditingButton.current
          ? discardButton.current
          : keepEditingButton.current
      next?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (opener?.isConnected) opener.focus({preventScroll: true})
    }
  }, [])

  return (
    <>
      <div
        className='modal fade show d-block'
        role='alertdialog'
        tabIndex={-1}
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        style={{zIndex: 1075}}
      >
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-body p-8 text-center'>
              <span
                className='d-inline-flex align-items-center justify-content-center rounded-circle p-5 mb-5 bg-light-warning text-warning'
                aria-hidden='true'
              >
                <KTIcon iconName='information-5' className='fs-2x' />
              </span>
              <h2 id={titleId} className='fs-3 fw-bolder mb-3'>
                پنجره بسته شود؟
              </h2>
              <p id={descriptionId} className='text-gray-600 mb-7 mx-auto' style={{maxWidth: 420}}>
                تغییراتی که در این پنجره داده‌اید ذخیره نشده است و با بستن پنجره از بین می‌رود.
              </p>
              <div className='d-flex flex-wrap justify-content-center gap-3'>
                <button
                  ref={keepEditingButton}
                  type='button'
                  className='btn btn-light'
                  onClick={onKeepEditing}
                >
                  ادامهٔ ویرایش
                </button>
                <button
                  ref={discardButton}
                  type='button'
                  className='btn btn-danger'
                  onClick={onDiscard}
                >
                  بستن بدون ذخیره
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' style={{zIndex: 1070}} />
    </>
  )
}

const DialogEscapeKey: FC = () => {
  const [askingFor, setAskingFor] = useState<HTMLElement>()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.repeat || event.isComposing) return
      if (event.defaultPrevented && !escapesFromClosedSelect.has(event)) return
      const target = event.target
      // The browser clears a filled search box on Escape.
      if (target instanceof HTMLInputElement && target.type === 'search' && target.value) return

      const dialog = topmostDialog()
      if (!dialog || isInReactBootstrapModal(dialog)) return
      const close = findDialogCloseControl(dialog)
      if (!close || isUnavailable(close)) return

      event.preventDefault()
      if (editedDialogs.has(dialog)) setAskingFor(dialog)
      else close.click()
    }

    document.addEventListener('input', onFieldEdited, true)
    document.addEventListener('change', onFieldEdited, true)
    document.addEventListener('click', onSelectOptionClicked, true)
    window.addEventListener('keydown', onKeyDownCapture, true)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('input', onFieldEdited, true)
      document.removeEventListener('change', onFieldEdited, true)
      document.removeEventListener('click', onSelectOptionClicked, true)
      window.removeEventListener('keydown', onKeyDownCapture, true)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  if (!askingFor) return null

  const discard = () => {
    setAskingFor(undefined)
    if (!askingFor.isConnected) return
    const close = findDialogCloseControl(askingFor)
    if (close && !isUnavailable(close)) close.click()
  }

  return <UnsavedChangesDialog onKeepEditing={() => setAskingFor(undefined)} onDiscard={discard} />
}

export {DialogEscapeKey}
